/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useContext, useEffect, useLayoutEffect, useRef } from "react";
import MagicBell, { FloatingNotificationInbox } from "@magicbell/magicbell-react";
import { useConfig, useBell } from "@magicbell/react-headless";
import { subscribe, isSupported, isSubscribed } from "@magicbell/webpush";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import NavContext from "components/molecules/Navigation/components/common/NavContext";
import { ReactComponent as NotificationIcon } from "assets/images/icons/TopNavIcons/NotificationIcon.svg";
import { NotificationPermissionTypes } from "../../../constants";

type NotificationsProps = {
  isStaff: boolean;
};

function Notifications(props: NotificationsProps) {
  const { isStaff } = props;
  const { navigationConfig } = useContext(NavContext);
  const config = useConfig();
  const { unreadCount }: any = useBell() || {};
  const notificationToggler = useRef<any>(null);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (notificationToggler.current) {
      notificationToggler.current();
    }
  }, []);

  useEffect(() => {
    const nav: any = window.navigator;
    if (nav?.setAppBadge) {
      if (unreadCount && unreadCount > 0) {
        nav.setAppBadge(unreadCount);
      } else {
        nav.clearAppBadge();
      }
    }
  }, [unreadCount]);

  useEffect(() => {
    if (!window.Notification || isStaff || !config.channels || !isSupported()) {
      return;
    }

    window.Notification.requestPermission().then(async (res) => {
      if (res === NotificationPermissionTypes.GRANTED) {
        const url = new URL(config?.channels?.webPush.config.subscribeUrl || "");
        const subscribeOptions = {
          host: "https://api.magicbell.com",
          project: String(url.searchParams.get("project")),
          token: String(url.searchParams.get("access_token")),
          serviceWorkerPath: "/service-worker.js",
        };
        const isAlreadySubscribed = await isSubscribed(subscribeOptions);
        if (!isAlreadySubscribed) {
          subscribe(subscribeOptions);
        }
      }
    });
  }, [isStaff, config]);

  const handleNotificationMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof SVGElement) {
      return;
    }
    if (notificationToggler.current) {
      notificationToggler.current();
    }
  };

  const navigateToActionUrl = (notification: any) => {
    if (notification.actionUrl) {
      navigate(`/${notification.actionUrl}`);
      if (notificationToggler.current) {
        notificationToggler.current();
      }
      return false;
    }
  };

  return (
    <div className="navigation-item" onClick={handleNotificationMenuClick}>
      <MagicBell BellIcon={<NotificationIcon />} defaultIsOpen bellCounter="unread">
        {(data) => {
          if (!notificationToggler.current) {
            notificationToggler.current = data.toggle;
          }
          return (
            <FloatingNotificationInbox
              placement="auto-start"
              layout={["header", "content"]}
              height={450}
              notificationPreferencesEnabled={false}
              onNotificationClick={navigateToActionUrl}
              hideArrow
              {...data}
            />
          );
        }}
      </MagicBell>
      {navigationConfig.expanded && (
        <span className="navigation-item-label">
          <FormattedMessage id="general.notifications" defaultMessage="Notifications" />
        </span>
      )}
    </div>
  );
}

export default Notifications;
